import { api } from '@goaltree/backend/convex/_generated/api'
import { Button } from '@goaltree/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@goaltree/ui/components/collapsible'
import { Sheet, SheetContent, SheetFooter } from '@goaltree/ui/components/sheet'
import { useQuery } from 'convex/react'
import { Archive, ArrowLeft, ChevronDown, ChevronRight, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type RichArchived = {
  title: string
  skippedCount?: number
  reason: 'skipped' | 'milestone-missed' | 'user-requested'
}

type RichAdded = {
  taskId?: string
  title: string
  targetTitle: string
  milestoneTitle: string
}

type RichChanges = {
  archived: RichArchived[]
  added: RichAdded[]
  kept: Array<{ title: string }>
  daysRemaining: number | null
  activeMilestoneTitle: string
}

interface ReplanChangesSheetProps {
  open: boolean
  onClose: () => void
  goalTitle: string
  changes: RichChanges
}

function Section({
  label,
  count,
  defaultOpen = true,
  children,
}: {
  label: string
  count: number
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 py-2 text-left">
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        <span className="text-[10px] font-bold text-muted-foreground">{count}</span>
        <span className="ml-auto text-muted-foreground">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </span>
      </CollapsibleTrigger>
      <div className="mb-1 h-px bg-border" />
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  )
}

export function ReplanChangesSheet({ open, onClose, goalTitle, changes }: ReplanChangesSheetProps) {
  const { t } = useTranslation()
  const todayCtx = useQuery(api.dailySurface.getTodayWithContext, {})
  const todayTasks = (todayCtx?.tasks ?? []).filter((t) => t.completedAt === null)

  function reasonLabel(item: RichArchived): string {
    if (item.reason === 'skipped' && item.skippedCount != null) {
      return t('common.replanReasonSkipped', { count: item.skippedCount })
    }
    if (item.reason === 'milestone-missed') return t('common.replanReasonMilestone')
    return t('common.replanReasonRequest')
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-full flex-col p-0 sm:max-w-md"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground">
              {t('common.replanSheetHeader')}
            </p>
            <p className="truncate text-sm font-semibold text-foreground">{goalTitle}</p>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-6">
          {/* Intro */}
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-foreground">
              ✦ {t('common.replanModalTitle')}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {changes.archived.length > 0
                ? t('common.replanSheetIntro')
                : t('common.replanSheetIntroFallback')}
            </p>
            {changes.daysRemaining != null && changes.daysRemaining > 0 && (
              <p className="text-xs text-muted-foreground">
                {t('common.replanSheetDaysRemaining', { count: changes.daysRemaining })}
              </p>
            )}
          </div>

          {/* Summary chips */}
          <div className="flex flex-wrap gap-2">
            {changes.archived.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs font-medium text-muted-foreground">
                <Archive className="size-3.5" />
                {t('common.replanSheetArchivedChip', { count: changes.archived.length })}
              </span>
            )}
            {changes.added.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs font-medium text-foreground">
                ✦ {t('common.replanSheetAddedChip', { count: changes.added.length })}
              </span>
            )}
            {changes.kept.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-3 py-1 text-xs font-medium text-muted-foreground">
                ✓ {t('common.replanSheetKeptChip', { count: changes.kept.length })}
              </span>
            )}
          </div>

          {/* Archived */}
          {changes.archived.length > 0 && (
            <Section label={t('common.replanSheetArchivedLabel')} count={changes.archived.length}>
              <ul className="mt-2 space-y-3">
                {changes.archived.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Archive className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-sm text-foreground leading-snug">{item.title}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <TriangleAlert className="size-3 shrink-0" />
                        {reasonLabel(item)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Added */}
          {changes.added.length > 0 && (
            <Section label={t('common.replanSheetAddedLabel')} count={changes.added.length}>
              <ul className="mt-2 space-y-3">
                {changes.added.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0 text-sm">✦</span>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground leading-snug">{item.title}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {t('common.replanSheetIn')} {item.targetTitle}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Kept */}
          {changes.kept.length > 0 && (
            <Section
              label={t('common.replanSheetKeptLabel')}
              count={changes.kept.length}
              defaultOpen={false}
            >
              <ul className="mt-2 space-y-2">
                {changes.kept.map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="shrink-0 text-sm">✓</span>
                    <p className="text-sm text-muted-foreground">{item.title}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Today's tasks */}
          {todayTasks.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {t('common.replanSheetTodayLabel')}
              </p>
              <div className="h-px bg-border" />
              <ul className="space-y-2">
                {todayTasks.map((item) => (
                  <li key={item.taskId} className="flex items-center gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-foreground/20" />
                    <p className="text-sm text-foreground">{item.title}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="border-t px-4 py-3">
          <Button onPress={onClose} className="w-full cursor-pointer">
            {t('common.replanKeepGoing')} →
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
